import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  Component,
  effect,
  inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileUploadSuccessEvent, WidgetComponent } from '@zcs/agent-chat-widget';
import { concatMap, from } from 'rxjs';
import { FolliaWidgetStoreService } from 'src/app/services/follia-widget/follia-widget-store.service';
import { FolliaWidgetService } from 'src/app/services/follia-widget/follia-widget.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { environment } from 'src/environments/environment';
import { ChatParameters, Context, OnBoardingInterface } from './chat-data-interface';

@Component({
  selector: 'app-app-follia-widget',
  standalone: false,
  templateUrl: './app-follia-widget.component.html',
  styleUrl: './app-follia-widget.component.scss',
})
export class AppFolliaWidgetComponent implements AfterViewInit, OnDestroy {
  /** Template del widget, renderizzato in un overlay CDK dedicato */
  @ViewChild('widgetTpl', { static: true }) widgetTpl!: TemplateRef<unknown>;

  /** Riferimento diretto al componente Widget dell'interfaccia (esiste solo dopo l'attach del portal) */
  @ViewChild('widget') widget!: WidgetComponent;

  private readonly widgetStore = inject(FolliaWidgetStoreService);
  private readonly folliaWidgetService = inject(FolliaWidgetService);
  private readonly utilityService = inject(UtilityService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  /**
   * Overlay CDK che ospita il widget: essendo un pane figlio diretto di
   * .cdk-overlay-container (sibling dei pane delle dialog) con z-index maggiorato,
   * il widget resta visibile anche sopra le dialog annidate.
   */
  private overlayRef?: OverlayRef;

  /** Icona SVG con gradiente utilizzata nel widget */
  protected readonly folliaIconSvgGradient = 'assets/svg/Logo_Follia_Gradient.svg';

  /** Host del server AI recuperato dall'ambiente */
  protected readonly HOST = environment.serverAI;
  protected readonly isDebugMode = !environment.production;

  /** Token di autenticazione corrente per la richiesta all'IA*/
  protected TOKEN: string = '';

  /** Configurazione CONTEXT per la chat AI */
  protected context: Context = {
    appName: '',
    prompt: '',
    datasourceIds: [],
  };

  /** Configurazione ONBOARDING per la chat AI */
  protected readonly CHAT_ONBOARDING_CONFIG: OnBoardingInterface = {
    content: {
      greeting:
        'Ciao! Sono Follia, il tuo assistente per la rendicontazione VSME.<br>' +
        'Posso aiutarti a compilare il report di sostenibilità, raccogliere i dati ESG, spiegarti gli indicatori dello standard VSME e guidarti tra i moduli Base e Completi.',
      suggestions: {
        title: '',
        items: [],
      },
      footerInfoText: '',
    },
  };

  /** Configurazione PARAMETERS per la chat AI */
  protected parameters: ChatParameters = {
    companyId: '',
    agentModule: 'SOSTENIBILE',
    llm: 'google_genai',
    tenantId: '',
    fallbackAgentInfoData: {
      agentTitle: 'Follia',
      agentDescription: 'Sostenibile.cloud',
    },
    onboardingConfig: this.CHAT_ONBOARDING_CONFIG,
    allowFileUpload: true,
    currentSection: 'virtual-assistant',
    loadFileByPost: true,
    maxUploadFileSize: 10 * 1024 * 1024,
  };

  protected interactions: any[] = [];

  /**
   * Funzione asincrona fornita al widget per il recupero del token.
   * * Gestisce il ciclo di vita del token verificandone la validità temporale
   * prima di ogni interazione. Se il token è mancante o scaduto, avvia
   * automaticamente la procedura di rinnovo, proteggendo l'applicazione
   * da loop infiniti tramite un sistema di tentativi limitati.
   * * @returns {Promise<string>} Il token JWT valido o l'ultimo token disponibile in caso di errore.
   */
  tokenAsyncFunction = async (): Promise<string> => {
    if (!(await this.getValidTokenAndSetWidget())) return '';

    return this.TOKEN;
  };

  log = console.log;

  /**
   * Reagisce all'aggiunta di nuovi ID sorgente dati (DataSource) dallo store.
   * Se ho nuovi dataSource, alla pusho nel context
   * @idDatasourceAi arriva dalla oggetto dell modulo appena creato, quindi con i documenti inclusi
   * @idDatasourceDefault arriva da chiamata EP che mi ritorna un id di un datasource di default
   * creato a mano dal portale della follia, questo per avere un datasource da usare anche prima di creare un modulo
   */
  private readonly _syncDataSourceEffect = effect(() => {
    const store = this.widgetStore;
    const currentIds = this.context.datasourceIds;

    const addIfMissing = (id: string | null | undefined) => {
      if (id && !currentIds.includes(id)) {
        currentIds.push(id);
      }
    };

    addIfMissing(store.idDatasourceAi());
    addIfMissing(store.idDatasourceDefault());
  });

  /**
   * Reagisce alla variazione del prompt modulo.
   * Se il context non ha nessun promp, aggiungo quello di default del modulo
   */
  private readonly _promptModulo = effect(() => {
    const promptModulo = this.widgetStore.promptModulo();
    if (!this.context.prompt) {
      this.context.prompt = promptModulo;
    }
  });

  /**
   * Subscribe all'observable che mi gestisce l'evento di sending alla chat
   * ES: Quando clicco su un icona per la compilazione elemento
   * Si scollega automaticamente alla distruzione del componente.
   */
  private readonly _interactionSub = this.widgetStore.sendInteraction$
    .pipe(
      takeUntilDestroyed(),
      // Trasformo la Promise (newConversation()) in un observable e aspetto la sua risposta
      concatMap(() => {
        if (this.widget) {
          return from(this.widget.newConversation());
        }
        return from(Promise.resolve()); // Se non c'È il widget ritorno una promise vuota
      }),
    )
    .subscribe(() => {
      this.sendingToChat(); // Dopo che il metodo newConversation() ha finito, procedo con i controlli e l'apertura della chat
    });

  /**
   * Crea l'overlay CDK in basso a destra e vi proietta il template del widget.
   */
  ngAfterViewInit(): void {
    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global().bottom('20px').right('20px'),
      hasBackdrop: false,
      panelClass: 'follia-widget-pane',
    });
    this.overlayRef.attach(new TemplatePortal(this.widgetTpl, this.viewContainerRef));
  }

  /**
   * Distrugge l'overlay (e quindi il widget) alla distruzione del componente,
   * es. alla chiusura della dialog che lo ospita.
   */
  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  /**
   * Gestisce l'evento di chiusura della chat inviato dal widget.
   */
  public onCloseChat(): void {
    this.log('ho chiuso');
  }

  /**
   * Gestisce l'evento di apertura della chat inviato dal widget.
   */
  public async onOpenChat() {
    this.log('ho aperto');
  }

  /**
   * Fornisce il token corrente (usato dal widget per le chiamate API).
   * @returns Il token JWT corrente.
   */
  public tokenFunction = (): string => this.TOKEN;

  /**
   * Gestisce gli eventi utente provenienti dal widget (upload file, click citazioni, etc.).
   * @param data Dati dell'evento ricevuto.
   */
  public onUserEvent(data: any): void {
    switch (data.type) {
      case 'file-upload-success':
        const fileData = data as FileUploadSuccessEvent;
        this.widget.sendInteraction({
          event: 'file-upload-success',
          parameters: { fileupload: fileData.data.id },
        });
        break;

      case 'citation-click':
        console.log('Citation clicked', data);
        break;

      case 'file-click':
        console.log('File clicked', data);
        break;
    }
  }

  /**
   * Prepara i dati e invia una richiesta di risposta al widget AI.
   * @private
   */
  private async sendingToChat(): Promise<void> {
    // 1. Genero il prompt per il Context
    this.updatePromptFromStore();

    // 2. Valida i requisiti necessari prima dell'invio del messaggio
    const canSend = await this.validatePreSending();
    if (!canSend) {
      return;
    }

    const isAllFileUploaded = await this.widgetStore.checkIfAllItemsAreProcessed();

    if (!isAllFileUploaded) {
      this.widgetStore.alertFileNotProcessed();
      return;
    }

    // 3. Genero il messaggio da visualizzare sulla chat
    const chatViewablePrompt =
      'Elabora una risposta per : ' + this.widgetStore.chatViewablePrompt();

    // 4. Attivo la chat e attendo la connessione del socket.
    //    openChat() invoca activateChat() senza await: il flag `activated` viene messo a true
    //    subito, ma ioService.connect() avviene solo dopo l'await del token. Senza questa
    //    attesa esplicita la sendInteraction successiva trova il socket undefined
    //    (TypeError: Cannot read properties of undefined (reading 'emit')).
    await this.widget.activateChat();

    // activateChat() rimette `activated` a false se la risoluzione del modulo/agent fallisce
    if (!this.widget.activated) {
      console.error('Attivazione della chat non riuscita: interazione non inviata.');
      return;
    }

    // 5. Apro il widget chat
    this.widget.openChat();

    // 6. Neccessario il setTimeout per fargli generare il .message class prima di fare l'Interaction
    //    Senno errore sullo scrollbar del widget
    setTimeout(() => {
      this.widget.sendInteraction({
        text: chatViewablePrompt,
        parameters: undefined,
      });
    }, 0);
  }

  /**
   * Aggiorna il prompt nel contesto concatenando i valori presenti nello store.
   * @private
   */
  private updatePromptFromStore(): void {
    const { promptModulo, promptTematica, promptElemento } = this.widgetStore;
    this.context.prompt = `${promptModulo()} ${promptTematica()} ${promptElemento()}`.trim();
  }

  /**
   * Controllo la validità del token, se è stato aggiornato
   * allora aggiorno anche i valori per il widget.
   * @returns
   */
  async getValidTokenAndSetWidget(): Promise<boolean> {
    const { success, jwt } = await this.widgetStore.getValidToken();

    if (!success || !jwt) return false;

    this.TOKEN = jwt.access_token;
    this.parameters.companyId = jwt.company;
    this.parameters.tenantId = jwt.tenant;

    return true;
  }

  /**
   * Valida i requisiti necessari prima dell'invio del messaggio
   * @returns Validità della configurazione attuale.
   * @private
   */
  private async validatePreSending(): Promise<boolean> {
    if (!(await this.getValidTokenAndSetWidget())) return false;

    const { tenantId, companyId } = this.parameters;

    const isValid = !!(this.HOST && this.TOKEN && tenantId && companyId);

    if (!isValid) {
      console.error('Validazione fallita: verificare i parametri della chat.');
      this.utilityService.openSnackBar(`Validazione fallita: parametri della chat non validi`, '', {
        duration: 3000,
        panelClass: ['red-snackbar'],
      });
    }

    return isValid;
  }

  /**
   * Metodo da usare per precompilare una domanda con la risposta del widget
   * Per capire se la risposta ha uno stream diverso, controllare interaction_id
   * @param response
   */
  protected pasteAiResponseToInput(response: string): void {
    /* this.log(response); */
  }
}
